import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Share2, Pin, Trash2, Tag, MoreHorizontal, Send, Paperclip, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { api } from "../lib/api";
import MarkdownText from "./MarkdownText";
import EmojiReactions from "./EmojiReactions";
import AttachmentGrid from "./AttachmentGrid";
import MentionTextarea from "./MentionTextarea";

const COMMENT_ATTACH_ACCEPT = "image/png,image/jpeg,image/webp,image/gif,application/pdf,video/mp4";

function AliasAvatar({ alias }) {
  const seed = (alias || "").slice(-4);
  const hue1 = (parseInt(seed, 36) % 360) || 350;
  const hue2 = (hue1 + 40) % 360;
  const initials = (alias || "??").split("-").pop().slice(0, 2).toUpperCase();
  return (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
      style={{ background: `linear-gradient(135deg, hsl(${hue1} 70% 45%), hsl(${hue2} 70% 35%))` }}
    >
      {initials}
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch { return ""; }
}

/**
 * Render markdown body with @-mention tokens stylized.
 *   - @Dealer-XXXX → pill
 *   - @[Name](user:USER_ID) → pill (Name only)
 */
function prepareBody(body) {
  if (!body) return "";
  // Replace @[Name](user:UUID) with plain @Name styled via markdown bold-italic
  let out = body.replace(/@\[([^\]]+)\]\(user:[a-f0-9-]{36}\)/g, (_, n) => `**@${n}**`);
  // Style alias mentions @Dealer-XXXX as bold
  out = out.replace(/@(Dealer-[A-HJ-NP-Z2-9]{4})\b/g, (_, a) => `**@${a}**`);
  return out;
}

export default function FeedCard({ post, scope = "community", currentAlias, isAdmin, onChange,
                                  expanded: expandedProp, onToggleExpanded }) {
  const isControlled = typeof expandedProp === "boolean";
  const [internalExpanded, setInternalExpanded] = useState(false);
  const showComments = isControlled ? expandedProp : internalExpanded;
  const setShowComments = (next) => {
    if (isControlled) onToggleExpanded?.(next);
    else setInternalExpanded(next);
  };
  const [comments, setComments] = useState(null);
  const [loadingComments, setLoadingComments] = useState(false);
  const [reply, setReply] = useState("");
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [uploadingReply, setUploadingReply] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // parent comment id when nested
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const replyFileRef = useRef(null);
  const replyInputRef = useRef(null);
  const viewMarkedRef = useRef(false);

  const apiBase = scope === "internal" ? "/internal/posts" : "/community/posts";
  const commentBase = scope === "internal" ? "/internal/comments" : "/community/comments";
  const isMine = post.author?.alias && post.author.alias === currentAlias;

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const toggleComments = async () => {
    const next = !showComments;
    setShowComments(next);
    // Mark post viewed on first expansion — drives the Unread tab.
    if (next && !viewMarkedRef.current) {
      viewMarkedRef.current = true;
      api.post(`${apiBase}/${post.id}/view`).catch(() => {});
    }
    if (next && comments === null) {
      setLoadingComments(true);
      try {
        const r = await api.get(`${apiBase}/${post.id}/comments`);
        setComments(r.data.comments || []);
      } catch (e) {
        console.error("comments load", e);
        setComments([]);
      } finally {
        setLoadingComments(false);
      }
    }
  };

  const onReactionChange = (reactions, my_reaction) => {
    const like_count = reactions["❤️"] || 0;
    onChange?.({
      ...post,
      reactions,
      my_reaction,
      like_count,
      liked_by_me: my_reaction === "❤️",
    });
  };

  const onCommentReactionChange = (commentId, reactions, my_reaction) => {
    setComments((cs) => (cs || []).map((c) => c.id === commentId ? { ...c, reactions, my_reaction } : c));
  };

  const share = async () => {
    const url = `${window.location.origin}/${scope}/posts/${post.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", { description: url });
    } catch { toast.error("Copy failed"); }
  };

  const uploadReplyFiles = async (files) => {
    if (!files || files.length === 0) return;
    const slots = 2 - replyAttachments.length;
    const accepted = Array.from(files).slice(0, slots);
    if (accepted.length === 0) {
      toast.error("Max 2 attachments per reply");
      return;
    }
    setUploadingReply(true);
    try {
      const uploaded = [];
      for (const f of accepted) {
        const form = new FormData();
        form.append("file", f);
        const r = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        const kind = r.data.kind === "image" ? "image" : r.data.kind === "video" ? "video" : "file";
        uploaded.push({ kind, url: r.data.url, filename: f.name || r.data.filename, size: r.data.size });
      }
      setReplyAttachments((arr) => [...arr, ...uploaded]);
    } catch (e) {
      console.error("reply upload failed", e);
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingReply(false);
      if (replyFileRef.current) replyFileRef.current.value = "";
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    const text = reply.trim();
    if (!text && replyAttachments.length === 0) return;
    setBusy(true);
    try {
      const r = await api.post(`${apiBase}/${post.id}/comments`, {
        body: text || " ",
        attachments: replyAttachments,
        parent_comment_id: replyingTo || null,
      });
      const newComment = r.data.comment || r.data;
      setComments(cs => [...(cs || []), newComment]);
      onChange?.({ ...post, comment_count: (post.comment_count || 0) + 1 });
      setReply("");
      setReplyAttachments([]);
      setReplyingTo(null);
    } catch (err) {
      console.error("comment failed", err);
      toast.error(err.response?.data?.detail || "Comment failed");
    } finally { setBusy(false); }
  };

  const startReply = (commentId) => {
    setReplyingTo(commentId);
    setTimeout(() => replyInputRef.current?.focus?.(), 50);
  };

  const pinPost = async () => {
    try {
      const r = await api.post(`${apiBase}/${post.id}/pin`);
      onChange?.({ ...post, pinned: r.data.pinned });
      toast.success(r.data.pinned ? "Pinned" : "Unpinned");
    } catch (e) { toast.error("Pin failed"); }
    setMenuOpen(false);
  };

  const deletePost = async () => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`${apiBase}/${post.id}`);
      onChange?.({ ...post, _deleted: true });
      toast.success("Post deleted");
    } catch (e) { toast.error("Delete failed"); }
    setMenuOpen(false);
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`${commentBase}/${commentId}`);
      setComments((cs) => (cs || []).filter((c) => c.id !== commentId));
      onChange?.({ ...post, comment_count: Math.max(0, (post.comment_count || 0) - 1) });
      toast.success("Comment deleted");
    } catch (e) { toast.error("Delete failed"); }
  };

  // Click anywhere on the card body (except interactive elements) → expand thread.
  const onCardClick = (e) => {
    // Ignore clicks that bubble from buttons, links, inputs, [contenteditable], or anything inside a [data-no-expand] wrapper.
    if (e.target.closest("button, a, input, textarea, select, [data-no-expand]")) return;
    if (showComments) return;
    toggleComments();
  };

  return (
    <article
      onClick={onCardClick}
      className="relative bg-white rounded-2xl border border-[#E5E7EB] p-5 sm:p-6 cursor-pointer shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.08)] hover:-translate-y-px transition-[box-shadow,transform] duration-200"
      data-testid={`feed-card-${post.id}`}
    >
      {post.pinned && (
        <div className="absolute top-0 right-5 -translate-y-1/2 bg-fc-red text-white text-[9px] tracking-[0.22em] font-black px-2 py-1 rounded">
          PINNED
        </div>
      )}

      {/* Header */}
      <header className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <AliasAvatar alias={post.author?.alias} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-[15px] text-fc-navy">{post.author?.alias}</span>
              {isMine && <span className="text-[9px] tracking-[0.18em] uppercase font-bold text-[#6B7280] bg-gray-100 px-1.5 py-0.5 rounded">YOU</span>}
              {post.author?.is_admin && <span className="text-[9px] tracking-[0.18em] uppercase font-bold text-fc-red bg-fc-red/10 px-1.5 py-0.5 rounded">ADMIN</span>}
            </div>
            <p className="text-[13px] text-[#6B7280] flex items-center gap-1.5 mt-0.5">
              <span className="inline-block w-1 h-1 rounded-full bg-[#6B7280]/40" />
              <span>{post.author?.rank}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[12px] text-[#6B7280] whitespace-nowrap">{timeAgo(post.created_at)}</span>
          {(isMine || isAdmin) && (
            <div className="relative" ref={menuRef}>
              <button onClick={() => setMenuOpen(o => !o)} className="text-[#6B7280] hover:text-fc-navy p-1 rounded hover:bg-gray-100" data-testid={`post-menu-${post.id}`} aria-label="Post menu">
                <MoreHorizontal size={16} />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg py-1 min-w-[140px] z-10">
                  {isAdmin && (
                    <button onClick={pinPost} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-fc-navy hover:bg-gray-50">
                      <Pin size={13} /> {post.pinned ? "Unpin" : "Pin"}
                    </button>
                  )}
                  {(isMine || isAdmin) && (
                    <button onClick={deletePost} className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-fc-red hover:bg-red-50">
                      <Trash2 size={13} /> Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Title */}
      {post.title && (
        <Link to={`/${scope}/posts/${post.id}`} className="block">
          <h3 className="text-[18px] font-bold leading-snug text-fc-navy hover:text-fc-red transition-colors mb-2">
            {post.title}
          </h3>
        </Link>
      )}

      {/* Body */}
      <div className="text-[15px] text-fc-navy/90 leading-relaxed">
        <MarkdownText variant="light">{prepareBody(post.body || "")}</MarkdownText>
      </div>

      {/* Attachments */}
      <AttachmentGrid attachments={post.attachments || []} testid={`post-attachments-${post.id}`} />

      {/* Course tag */}
      {post.course_tag && (
        <Link
          to={`/courses/${post.course_tag}`}
          className="inline-flex items-center gap-1 mt-3 text-[11px] tracking-[0.18em] uppercase font-bold text-fc-red border border-fc-red/30 px-2.5 py-1 rounded-full hover:bg-fc-red hover:text-white transition-colors"
          data-testid={`post-course-tag-${post.id}`}
        >
          <Tag size={10} /> {post.course_title || post.course_tag}
        </Link>
      )}

      {/* Footer actions */}
      <footer className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 gap-3 flex-wrap">
        <EmojiReactions
          scope={scope}
          target="post"
          id={post.id}
          reactions={post.reactions || {}}
          myReaction={post.my_reaction}
          onChange={onReactionChange}
        />
        <div className="flex items-center gap-1">
          <button
            onClick={toggleComments}
            className={`flex items-center gap-1.5 text-sm font-medium px-2.5 py-1.5 rounded-lg transition-colors ${showComments ? "text-fc-red bg-fc-red/5" : "text-[#6B7280] hover:bg-gray-100"}`}
            data-testid={`comment-toggle-${post.id}`}
          >
            <MessageSquare size={16} /> {post.comment_count || 0}
          </button>
          <button
            onClick={share}
            className="flex items-center gap-1.5 text-sm font-medium px-2.5 py-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100"
            data-testid={`share-${post.id}`}
          >
            <Share2 size={16} /> <span className="hidden sm:inline">Share</span>
          </button>
        </div>
      </footer>

      {/* Comments thread */}
      {showComments && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-4" data-testid={`comments-${post.id}`} data-no-expand>
          {loadingComments && <p className="text-sm text-[#6B7280]">Loading comments…</p>}
          {!loadingComments && comments && comments.length === 0 && (
            <p className="text-sm text-[#6B7280]">No comments yet. Be the first to reply.</p>
          )}
          {/* Group: top-level first (oldest first inside group, newest topic groups first to match feed cadence). */}
          {(() => {
            const all = comments || [];
            const topLevel = all.filter((c) => !c.parent_comment_id);
            const repliesBy = {};
            for (const r of all) {
              if (r.parent_comment_id) {
                (repliesBy[r.parent_comment_id] ||= []).push(r);
              }
            }
            // Top-level: chronological (oldest first) — easier to follow a thread.
            topLevel.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
            return topLevel.map((c) => {
              const mineComment = c.author?.alias === currentAlias;
              const replies = (repliesBy[c.id] || []).slice().sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
              return (
                <div key={c.id} data-testid={`comment-${c.id}`}>
                  <CommentRow
                    c={c} scope={scope} mineComment={mineComment}
                    isAdmin={isAdmin}
                    onReply={() => startReply(c.id)}
                    onDelete={() => deleteComment(c.id)}
                    onReactionChange={(rx, my) => onCommentReactionChange(c.id, rx, my)}
                  />
                  {/* Nested replies */}
                  {replies.length > 0 && (
                    <div className="ml-8 mt-3 space-y-3 border-l-2 border-fc-red/30 pl-4" data-testid={`replies-${c.id}`}>
                      {replies.map((rep) => (
                        <CommentRow
                          key={rep.id} c={rep} scope={scope}
                          mineComment={rep.author?.alias === currentAlias}
                          isAdmin={isAdmin} nested
                          onReply={() => startReply(c.id)}  /* replies to a reply attach to parent */
                          onDelete={() => deleteComment(rep.id)}
                          onReactionChange={(rx, my) => onCommentReactionChange(rep.id, rx, my)}
                        />
                      ))}
                    </div>
                  )}
                  {/* Inline reply composer slot — shows under the comment being replied to */}
                  {replyingTo === c.id && (
                    <div className="ml-8 mt-3 border-l-2 border-fc-red/30 pl-4">
                      <ReplyComposer
                        inputRef={replyInputRef}
                        scope={scope} currentAlias={currentAlias}
                        reply={reply} setReply={setReply}
                        replyAttachments={replyAttachments} setReplyAttachments={setReplyAttachments}
                        uploadingReply={uploadingReply} replyFileRef={replyFileRef}
                        uploadReplyFiles={uploadReplyFiles}
                        busy={busy} submitComment={submitComment}
                        cancel={() => { setReplyingTo(null); setReply(""); setReplyAttachments([]); }}
                        placeholder={`Reply to ${c.author?.alias}…`}
                        nested
                      />
                    </div>
                  )}
                </div>
              );
            });
          })()}

          {/* Top-level reply composer */}
          {replyingTo === null && (
            <ReplyComposer
              inputRef={replyInputRef}
              scope={scope} currentAlias={currentAlias}
              reply={reply} setReply={setReply}
              replyAttachments={replyAttachments} setReplyAttachments={setReplyAttachments}
              uploadingReply={uploadingReply} replyFileRef={replyFileRef}
              uploadReplyFiles={uploadReplyFiles}
              busy={busy} submitComment={submitComment}
              placeholder="Reply… Use @ to tag someone"
              testidSuffix={post.id}
            />
          )}
        </div>
      )}
    </article>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────
function CommentRow({ c, scope, mineComment, isAdmin, onReply, onDelete, onReactionChange, nested = false }) {
  const avatarScale = nested ? "scale-[0.6]" : "scale-75";
  return (
    <div className="flex items-start gap-2.5" data-no-expand>
      <div className={`${avatarScale} origin-top-left -mr-2`}><AliasAvatar alias={c.author?.alias} /></div>
      <div className="flex-1 min-w-0">
        <div className={`bg-gray-50 rounded-lg px-3 py-2 ${nested ? "text-[13px]" : ""}`}>
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-[13px] text-fc-navy">{c.author?.alias}</span>
              <span className="text-[11px] text-[#6B7280]">{timeAgo(c.created_at)}</span>
              {nested && <span className="text-[9px] tracking-[0.18em] uppercase font-black text-fc-red bg-fc-red/10 px-1 py-0.5 rounded">REPLY</span>}
            </div>
            {(mineComment || isAdmin) && (
              <button
                onClick={onDelete}
                className="text-[#6B7280] hover:text-fc-red"
                data-testid={`comment-delete-${c.id}`}
                aria-label="Delete comment"
              >
                <Trash2 size={11} />
              </button>
            )}
          </div>
          <div className="text-[14px] text-fc-navy/90 leading-relaxed">
            <MarkdownText variant="light">{prepareBody(c.body || "")}</MarkdownText>
          </div>
          <AttachmentGrid attachments={c.attachments || []} testid={`comment-attachments-${c.id}`} />
        </div>
        <div className="mt-1.5 ml-1 flex items-center gap-2">
          <EmojiReactions
            scope={scope} target="comment" id={c.id}
            reactions={c.reactions || {}} myReaction={c.my_reaction}
            onChange={onReactionChange} size="sm"
          />
          <button
            type="button"
            onClick={onReply}
            className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#6B7280] hover:text-fc-red transition-colors px-2 py-1 rounded"
            data-testid={`comment-reply-btn-${c.id}`}
          >
            Reply
          </button>
        </div>
      </div>
    </div>
  );
}

function ReplyComposer({
  inputRef, scope, currentAlias, reply, setReply,
  replyAttachments, setReplyAttachments, uploadingReply, replyFileRef, uploadReplyFiles,
  busy, submitComment, cancel, placeholder, testidSuffix, nested = false,
}) {
  return (
    <form onSubmit={submitComment} className="space-y-2 pt-2" data-no-expand>
      <div className="flex items-start gap-2.5">
        <div className={`${nested ? "scale-[0.6]" : "scale-75"} origin-top-left -mr-2`}><AliasAvatar alias={currentAlias} /></div>
        <div className="flex-1">
          <MentionTextarea
            ref={inputRef}
            value={reply} onChange={setReply} scope={scope}
            placeholder={placeholder} rows={2} maxLength={4000}
            className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-fc-red resize-none placeholder:text-gray-400 text-fc-navy"
            data-testid={`comment-input-${testidSuffix || "nested"}`}
          />
          {replyAttachments.length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap" data-testid={`comment-attachments-preview-${testidSuffix || "nested"}`}>
              {replyAttachments.map((a, idx) => (
                <div key={a.url + idx} className="relative group rounded-md overflow-hidden border border-[#E5E7EB] bg-gray-50 w-20 h-20">
                  {a.kind === "image" ? (
                    <img src={/^https?:\/\//.test(a.url) ? a.url : `${process.env.REACT_APP_BACKEND_URL || ""}${a.url}`} alt={a.filename} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-[10px] text-fc-navy p-1 text-center">
                      <Paperclip size={12} className="text-fc-red mb-0.5" />
                      <span className="truncate w-full">{a.filename}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setReplyAttachments((arr) => arr.filter((_, i) => i !== idx))}
                    className="absolute top-0.5 right-0.5 bg-fc-red text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px]"
                    aria-label="Remove"
                  >
                    <X size={9} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between gap-2 mt-2">
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => replyFileRef.current?.click()}
                disabled={uploadingReply || replyAttachments.length >= 2}
                className="w-7 h-7 rounded-full flex items-center justify-center text-[#6B7280] hover:bg-fc-red/10 hover:text-fc-red transition-colors disabled:opacity-40"
                title="Attach"
                aria-label="Attach"
              >
                {uploadingReply ? <Loader2 className="animate-spin" size={13} /> : <ImageIcon size={13} />}
              </button>
              <input
                ref={replyFileRef}
                type="file"
                accept={COMMENT_ATTACH_ACCEPT}
                multiple
                className="hidden"
                onChange={(e) => uploadReplyFiles(e.target.files)}
              />
            </div>
            <div className="flex items-center gap-2">
              {cancel && (
                <button type="button" onClick={cancel} className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#6B7280] hover:text-fc-navy px-2">
                  Cancel
                </button>
              )}
              <button type="submit" disabled={busy || uploadingReply || (!reply.trim() && replyAttachments.length === 0)} className="btn-fc-red text-xs px-3 py-2 disabled:opacity-40" data-testid={`comment-submit-${testidSuffix || "nested"}`}>
                <Send size={12} /> {nested ? "Reply" : "Comment"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}
